import { ReactNode, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { usePublicPlatformMaintenance } from '@/hooks/usePublicPlatformMaintenance';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileRole } from '@/hooks/useProfileRole';
import { isPlatformAdminRole } from '@/lib/platformRole';

interface PlatformGuardProps {
    children: ReactNode;
}

/**
 * PlatformGuard
 * 
 * Checks maintenance via public API (avoids PostgREST 401 on platform_settings for non-admins).
 * If maintenance is ON → non-admin users are redirected to /maintenance.
 * Admin users can always access the platform.
 * 
 * Place this as a wrapper around your main Routes in App.tsx.
 */
export function PlatformGuard({ children }: PlatformGuardProps) {
    const { data: settings, isPending: maintenanceLoading } = usePublicPlatformMaintenance();
    const { user, loading: authLoading } = useAuth();
    const userId = user?.id ?? null;
    const { data: role, isPending: rolePending, isFetched } = useProfileRole();

    const isAdmin = useMemo((): boolean | null => {
        if (!userId) return false;
        if (rolePending && !isFetched) return null;
        return isPlatformAdminRole(role);
    }, [userId, role, rolePending, isFetched]);

    // Don't block render while checking — let the app load normally
    // Only redirect once we have confirmed: maintenance=true AND user is not admin
    if (maintenanceLoading || authLoading || isAdmin === null) {
        return <>{children}</>;
    }

    const isMaintenance = settings?.maintenance_mode === true;

    if (isMaintenance && !isAdmin) {
        return <Navigate to="/maintenance" replace />;
    }

    return <>{children}</>;
}
